import { createColumnHelper } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { Container, Form } from "react-bootstrap";
import { Link } from "react-router";

import { DataTable } from "@/components/DataTable";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageLoader } from "@/components/PageLoader";
import { TableWrapper } from "@/components/TableWrapper";
import { useTrackedQuery } from "@/hooks/useTrackedQuery";
import { NCRJob, ncrJobsQueryOptions } from "./queries/ncr-jobs";

type TableRow = NCRJob;

const columnHelper = createColumnHelper<TableRow>();

const columns = [
  columnHelper.accessor("number", {
    header: "NCR #",
    cell: ({ getValue }) => (
      <Link to={`/job/${getValue()}/details`} className="stretched-link">
        {getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor("description", { header: "Notes", meta: { cellProps: { className: "text-pre-line" } } }),
  columnHelper.accessor("closed", {
    header: "Status",
    cell: ({ getValue }) => (getValue() ? "Inactive" : "Active"),
  }),
];

export const NCRLog: React.FC = () => {
  // Define State
  const [showClosed, setShowClosed] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);

  // Define callbacks
  const toggleShowClosed = useCallback(() => {
    setShowClosed(!showClosed);
  }, [showClosed]);
  const toggleShowArchived = useCallback(() => {
    setShowArchived(!showArchived);
  }, [showArchived]);

  // Fetch data
  const jobQuery = useTrackedQuery(ncrJobsQueryOptions);

  // Filter and map query data into table format
  // NOTE: We aren't using the 'select' option in the query because we want to show the user the number of unfiltered records
  // Note: Use the useMemo hook here so that we aren't recomputing the table data on every render
  const tableData = useMemo((): TableRow[] => {
    return (jobQuery.data ?? []).filter((x) => showClosed || !x.closed).filter((x) => showArchived || !x.archived);
  }, [jobQuery.data, showClosed, showArchived]);

  // Calculate some number for displaying on the filters
  const numClosed = jobQuery.data?.filter((x) => x.closed).length ?? 0;
  const numArchived = jobQuery.data?.filter((x) => x.archived).length ?? 0;

  // Handle loading and error states
  if (jobQuery.isError) return `Error loading NCRs: ${jobQuery.error.message}`;
  if (jobQuery.isPending) return <PageLoader text="Loading NCRs" />;

  return (
    <ErrorBoundary>
      <Container fluid className="py-3 h-100 d-flex flex-column">
        <div className="col-auto">
          <h2>
            NCRs{" "}
            <small className="text-body-secondary">
              (Displaying {tableData.length} of {jobQuery.data.length})
            </small>
          </h2>
          <p className="lead">Click on a row to load the detailed tracker for that NCR.</p>
          <div className="overflow-hidden mb-3">
            <div className="row row-cols-auto g-4 align-items-center">
              <span>Filters:</span>
              <Form.Check
                type="switch"
                label={`Show Closed (${numClosed.toFixed()})`}
                checked={showClosed}
                onChange={toggleShowClosed}
              />
              <Form.Check
                type="switch"
                label={`Show Archived (${numArchived.toFixed()})`}
                checked={showArchived}
                onChange={toggleShowArchived}
              />
            </div>
          </div>
        </div>
        <TableWrapper className="col">
          <DataTable
            bordered
            hover
            columns={columns}
            data={tableData}
            initialState={{
              sorting: [{ id: "number", desc: true }],
            }}
            // Add relative positioning to the data rows so that we can use the 'stretched-link' class
            meta={{ dataRowProps: { className: "position-relative" } }}
          />
        </TableWrapper>
      </Container>
    </ErrorBoundary>
  );
};
